/**
 * Founding Customer Programme application form.
 *
 * A normal storefront form on a normal page — NOT part of the configurator web
 * component. Submits as JSON to the Worker's `/founding` route via the signed
 * Shopify app proxy (the same proxy the configurator uses for quotes), so the
 * research answers land in the team inbox with no storage and no new backend.
 *
 * Progressive enhancement: the fields carry native `required`, so the browser
 * blocks an incomplete submit even if this handler never binds. When the proxy
 * isn't wired yet (`data-endpoint` empty) it runs in MOCK mode so the page
 * previews before deploy. Honeypot: a filled `website` field = a bot; we fake a
 * success so it gets no signal.
 */

const FIELDS = ['name', 'email', 'making', 'size', 'quantity', 'location', 'replacing', 'lookedElsewhere', 'hesitation', 'heard'];

function setStatus(form, kind, message) {
  const status = form.querySelector('[data-founding-status]');
  if (!status) return;
  status.hidden = false;
  status.dataset.kind = kind; // 'ok' | 'error' — styled in the section CSS
  status.textContent = message;
}

/**
 * Gate the submit button on the two required consents: disabled until both are
 * ticked. No-ops while a send is in flight (the send owns the button then), so
 * a stray change event can't re-enable a "Sending…" button. Called on every
 * consent change and after a reset, so re-enabling and re-disabling both happen.
 */
function refreshGate(form) {
  const btn = form.querySelector('[data-founding-submit]');
  if (!btn || form.dataset.sending === 'true') return;
  const consents = form.querySelectorAll('input[type="checkbox"][required]');
  btn.disabled = ![...consents].every((c) => c.checked);
}

/**
 * Success = replace the whole form with a loud confirmation panel and scroll to
 * it. The old thin status line got missed, so people resubmitted; swapping the
 * form out removes any doubt AND makes a second submit impossible (form's gone).
 */
function showThanks(form, message) {
  form.dataset.sending = 'false';
  const done = form.closest('.foundp__apply')?.querySelector('[data-founding-done]');
  if (!done) {
    setStatus(form, 'ok', message); // fallback: inline confirmation
    return;
  }
  const msg = done.querySelector('[data-founding-done-msg]');
  if (msg) msg.textContent = message;
  form.hidden = true;
  done.hidden = false;
  done.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function submit(form) {
  const endpoint = form.dataset.endpoint || '';
  const get = (name) => form.querySelector(`[name="${name}"]`)?.value?.trim() ?? '';

  const thanks = form.dataset.thanks || "Thanks — your application is in. We'll come back to everyone either way.";

  // Honeypot: a real person never fills the hidden field. Fake success.
  if (get('website') !== '') {
    showThanks(form, thanks);
    return;
  }

  const payload = { website: '' };
  for (const f of FIELDS) payload[f] = get(f);
  payload.consentPhotos = form.querySelector('[name="consentPhotos"]')?.checked || false;
  payload.consentTerms = form.querySelector('[name="consentTerms"]')?.checked || false;

  const btn = form.querySelector('[data-founding-submit]');
  const original = btn?.textContent;
  form.dataset.sending = 'true';
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Sending…';
  }

  // Error path only — the form stays put so they can retry; success swaps the
  // form out via showThanks(), so there's nothing to restore there.
  const fail = (message) => {
    if (btn) btn.textContent = original;
    form.dataset.sending = 'false';
    refreshGate(form); // consents are still ticked → button re-enables
    setStatus(form, 'error', message);
  };
  const errorMsg = form.dataset.error || 'Sorry, something went wrong. Please email us instead and we\'ll sort it.';

  // Mock mode — no proxy wired. Let the page be demoed end-to-end pre-deploy.
  if (!endpoint) {
    showThanks(form, form.dataset.thanks || 'Thanks — your application is in. (Preview mode: nothing was sent.)');
    return;
  }

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.ok) showThanks(form, thanks);
    else fail(errorMsg);
  } catch {
    fail(errorMsg);
  }
}

document.querySelectorAll('[data-founding-form]').forEach((form) => {
  // Gate the button on the consents, live.
  form.querySelectorAll('input[type="checkbox"][required]').forEach((c) =>
    c.addEventListener('change', () => refreshGate(form))
  );
  refreshGate(form); // start disabled until both are ticked

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (form.dataset.sending === 'true') return; // a request is already in flight
    if (form.checkValidity()) {
      submit(form);
    } else {
      // Turn on the red invalid styling and jump focus to the first empty field.
      form.dataset.validated = 'true';
      form.reportValidity();
    }
  });
});
